/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import Spinner from "../components/Spinner";
import DisplayFromFHIRDataRequirement from "../datatypeComponents/DataRequirementDisplay";
import DisplayFromFHIRParameterDefinition from "../datatypeComponents/ParameterDefinitionDisplay";
import { DisplayFromFHIRAttachment } from "../datatypeComponents/AttachmentEntry";
import { DisplayFromFHIRRelatedArtifact } from "../datatypeComponents/RelatedArtifactEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import { activityDefinitionDotSubjectCodeableConceptValueSet } from "./ActivityDefinitionResourceDisplay";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import StringEntry from "../datatypeComponents/StringEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";

const libraryDotTypeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/library-type", code: "logic-library", display: "Logic Library" },
    { system: "http://terminology.hl7.org/CodeSystem/library-type", code: "model-definition", display: "Model Definition" },
    { system: "http://terminology.hl7.org/CodeSystem/library-type", code: "asset-collection", display: "Asset Collection" },
    { system: "http://terminology.hl7.org/CodeSystem/library-type", code: "module-definition", display: "Module Definition" }
];
const libraryDotSubjectReferenceResourceTypes = ['Group'];
const libraryDotSubjectAllowedDatatypes = ['CodeableConcept', 'Reference'];

const generateNarrativeForLibraryResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p>";
        }
        if (resource.subtitle && typeof resource.type === "string") {
            innerDiv += "<p><b>Subtitle: </b>" + resource.subtitle + "</p>";
        }
        if (resource.name) {
            innerDiv += "<p><b>Name: </b>" + resource.name + "</p>";
        }
        if (resource.description) {
            innerDiv += "<p><b>Description: </b>" + resource.description + "</p>";
        }
        if (resource.type) {
            innerDiv += "<p><b>Type: </b>" + displayStringFromFHIRCodeableConcept(resource.type) + "</p>";
        }
        if (resource.subjectCodeableConcept) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRCodeableConcept(resource.subjectCodeableConcept) + "</p>";
        }
        if (resource.subjectReference) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(resource.subjectReference) + "</p>";
        }
        if (resource.relatedArtifact?.length > 0) {
            innerDiv += resource.relatedArtifact.map((item) => {
                let type = item.type || "";
                let display = item.display || "";
                let citation = item.citation || "";
                let resource = item.resource || "";
                let resourceReference = displayStringFromFHIRReference(item.resourceReference) || "";
                return "<p><b>Item (" + type + "): </b>" + display + " " + citation + " " + resource + " " + resourceReference + "</p>"
            }).join("");
        }
        if (resource.content?.length > 0) {
            innerDiv += resource.content.map((item) => {
                let title = item.title || "";
                let url = item.url || "";
                return "<p><b>Item: </b>" + title + " " + url + "</p>"
            }).join("");
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + innerDiv + "</div>" };
}

const LibraryDisplay = ({ foi }) => {
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const [libraryResourceState, setLibraryResourceState] = useState({});
    const [subjectDatatypeState, setSubjectDatatypeState] = useState("none");
    const [libraryContainedItemsState, setLibraryContainedItemsState] = useState({ "references": [], "loaded": false });

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Summary", "Title", "Subject", "Content", "Parameters"]));
        dispatch(changeBrowserTabTitle("FEvIR Library - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setLibraryResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setLibraryResourceState(resourceJson);
            if (resourceJson.subjectReference) { setSubjectDatatypeState("Reference"); }
            else if (resourceJson.subjectCodeableConcept) { setSubjectDatatypeState("CodeableConcept"); }
            if (!libraryContainedItemsState?.loaded) {
                let libraryContainedItemReferences = [];
                let otherRelatedArtifactItems = [];
                if (resourceJson.relatedArtifact) {
                    for (const relatedArtifact of resourceJson.relatedArtifact) {
                        if (relatedArtifact.type === "contains") {
                            libraryContainedItemReferences.push(relatedArtifact.resourceReference);
                        } else {
                            otherRelatedArtifactItems.push(relatedArtifact);
                        }
                    }
                }
                let setProfile;
                if (resourceJson.meta?.profile?.[0]) {
                    let libraryProfile = resourceJson.meta.profile[0];
                    if (libraryProfile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/search-results") {
                        setProfile = "SearchStrategy";
                    }
                    if (libraryProfile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/systematic-review-included-studies") {
                        setProfile = "SystematicReviewEligibilityCriteria";
                    }
                    if (libraryProfile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/systematic-review-excluded-studies") {
                        setProfile = "SystematicReviewEligibilityCriteria";
                    }
                }
                setLibraryContainedItemsState({
                    "libraryContainedItemReferences": libraryContainedItemReferences, "loaded": true,
                    "setProfile": setProfile,
                    "otherRelatedArtifactItems": otherRelatedArtifactItems
                });
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Library " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (libraryContainedItemsState?.libraryContainedItemReferences?.length) {
            let libraryContainedItems = libraryContainedItemsState.libraryContainedItemReferences.map(entry => {
                return { "type": "contains", "resourceReference": entry }
            });
            let newRelatedArtifact = libraryContainedItemsState.otherRelatedArtifactItems.concat(libraryContainedItems);
            setLibraryResourceState(prevState => { return { ...prevState, relatedArtifact: newRelatedArtifact } });
        }
    }, [libraryContainedItemsState]);

    useEffect((() => {
        if (subjectDatatypeState === 'CodeableConcept') {
            setLibraryResourceState(prevState => { return { ...prevState, 'subjectReference': null } });
        }
        if (subjectDatatypeState === 'Reference') {
            setLibraryResourceState(prevState => { return { ...prevState, 'subjectCodeableConcept': null } });
        }
    }), [subjectDatatypeState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(libraryResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = libraryResourceState.title;
            newResourceDetail.displayTitle = libraryResourceState.title ||
                libraryResourceState.name || newResourceDetail.displayTitle;
            if (libraryResourceState.title !== undefined &&
                previousFinalValue !== libraryResourceState.title) {
                //console.log("resourceUpdate for Library.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    libraryResourceState.title);
            }
        }
    }, [libraryResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = libraryResourceState.name;
            newResourceDetail.displayTitle = libraryResourceState.title ||
                libraryResourceState.name || newResourceDetail.displayTitle;
            if (libraryResourceState.name !== undefined &&
                previousFinalValue !== libraryResourceState.name) {
                //console.log("resourceUpdate for Library.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "name",
                    previousInitialValue,
                    libraryResourceState.name);
            }
        }
    }, [libraryResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "subtitle", "string", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.subtitle]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "subjectCodeableConcept", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.subjectCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "subjectReference", "Reference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.subjectReference]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "type", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "relatedArtifact", "ArrayOfRelatedArtifact", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.relatedArtifact]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(libraryResourceState, "content", "ArrayOfAttachment", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [libraryResourceState.content]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {libraryResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                    generatedNarrative={generateNarrativeForLibraryResource(libraryResourceState)} startCollapsed
                                    startingValue={libraryResourceState.text} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {(libraryResourceState.text?.div && libraryResourceState.text.status && libraryResourceState.text.status !== "empty") ?
                                    <DisplayFromFHIRXhtml xhtml={libraryResourceState.text.div} />
                                    : <p>[Narrative summary not generated.]</p>}
                            </>
                    }
                </div>
                <h3 id="title">Title</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <StringEntry elementName='title' fieldLabel='Title'
                                    startingValue={libraryResourceState.title} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {libraryResourceState.title && <p><b>Title: </b>{libraryResourceState.title}</p>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <StringEntry elementName='subtitle' fieldLabel='Subtitle'
                                    startingValue={libraryResourceState.subtitle} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {libraryResourceState.subtitle && <p><b>Subtitle: </b>{libraryResourceState.subtitle}</p>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <StringEntry elementName='name' fieldLabel='Name'
                                    startingValue={libraryResourceState.name} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {libraryResourceState.name && <p><b>Name: </b>{libraryResourceState.name}</p>}
                            </>
                    }
                    {libraryResourceState.name && <p><b>Name: </b>{libraryResourceState.name}</p>}
                </div>
                <h3 id="subject">Subject</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <MarkdownEntry elementName='description' fieldLabel='Description'
                                    startCollapsed
                                    startingValue={libraryResourceState.description} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {libraryResourceState.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={libraryResourceState.description} /><br /><br /></div>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                                    valueSet={libraryDotTypeValueSet} startCollapsed
                                    startingValue={libraryResourceState.type} setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {libraryResourceState.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={libraryResourceState.type} /></span>}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <p><b>Subject:</b></p>
                                <div style={{ marginLeft: "24px" }}>
                                    <DatatypeSelector elementXName='subject[x]' allowedDatatypes={libraryDotSubjectAllowedDatatypes}
                                        datatypeState={subjectDatatypeState} setDatatypeState={setSubjectDatatypeState} />
                                    {(subjectDatatypeState === 'CodeableConcept') &&
                                        <CodeableConceptEntry elementName='subjectCodeableConcept'
                                            fieldLabel='Subject (as CodeableConcept)'
                                            startingValue={libraryResourceState.subjectCodeableConcept || null}
                                            startCollapsed
                                            valueSet={activityDefinitionDotSubjectCodeableConceptValueSet}
                                            setResourceState={setLibraryResourceState} />}
                                    {(subjectDatatypeState === 'Reference') &&
                                        <ReferenceEntry elementName='subjectReference' fieldLabel='Subject (as Reference)'
                                            referencedResourceTypes={libraryDotSubjectReferenceResourceTypes}
                                            startCollapsed
                                            startingValue={libraryResourceState.subjectReference || null}
                                            setResourceState={setLibraryResourceState} />}
                                </div>
                            </>
                            :
                            <>
                                {libraryResourceState.subjectCodeableConcept && <span><b>Subject: </b><DisplayFromFHIRCodeableConcept codeableConcept={libraryResourceState.subjectCodeableConcept} /></span>}
                                {libraryResourceState.subjectReference && <p><b>Subject: </b><DisplayFromFHIRReference reference={libraryResourceState.subjectReference} /></p>}
                            </>
                    }
                </div>
                <h3 id="content">Content</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                {libraryContainedItemsState?.loaded && <>
                                    <p>To manage the content entries as a simple set of resource references:</p>
                                    <div style={{ marginLeft: "24px" }}>
                                        <ArrayEntry datatype='Reference' elementName='libraryContainedItemReferences'
                                            fieldLabel={(libraryContainedItemsState.setProfile ||
                                                'The') + ' Resource'}
                                            startingValue={libraryContainedItemsState.libraryContainedItemReferences || []}
                                            setProfile={libraryContainedItemsState.setProfile}
                                            startCollapsed startEmptyArrayClosed enableCreation={true}
                                            setResourceState={setLibraryContainedItemsState} />
                                    </div>
                                </>}
                                <ArrayEntry datatype='RelatedArtifact' elementName='relatedArtifact'
                                    allowedTypeValues="FHIR" startCollapsed deletable={true}
                                    fieldLabel='Item (as Related Artifact)'
                                    startingValue={libraryResourceState.relatedArtifact}
                                    setResourceState={setLibraryResourceState} />

                            </>
                            :
                            <>
                                {Array.isArray(libraryResourceState.relatedArtifact) &&
                                    libraryResourceState.relatedArtifact.map((relatedArtifact, relatedArtifactIndex) => {
                                        return <div key={relatedArtifactIndex}>
                                            <b>Item: </b><DisplayFromFHIRRelatedArtifact relatedArtifact={relatedArtifact} />
                                        </div>
                                    })}
                            </>
                    }
                    {
                        editMode ?
                            <>
                                <ArrayEntry datatype='Attachment' elementName='content' startCollapsed
                                    fieldLabel='Item (as Attachment)'
                                    startingValue={libraryResourceState.content}
                                    setResourceState={setLibraryResourceState} />
                            </>
                            :
                            <>
                                {Array.isArray(libraryResourceState.content) &&
                                    libraryResourceState.content.map((content, contentIndex) => {
                                        return <div key={contentIndex}>
                                            <b>Attachment: </b><DisplayFromFHIRAttachment attachment={content} />
                                        </div>
                                    })}
                            </>
                    }
                </div>
                <h3 id="parameters">Parameters</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <p>Data entry interface not created for parameter or dataRequirement elements.</p>
                            </>
                            :
                            <>
                                {Array.isArray(libraryResourceState.parameter) &&
                                    libraryResourceState.parameter.map((parameter, parameterIndex) => {
                                        return <div key={parameterIndex}>
                                            <b>Parameter: </b><DisplayFromFHIRParameterDefinition parameterDefinition={parameter} />
                                        </div>
                                    })}
                                {Array.isArray(libraryResourceState.dataRequirement) &&
                                    libraryResourceState.dataRequirement.map((dataRequirement, dataRequirementIndex) => {
                                        return <div key={dataRequirementIndex}>
                                            <b>Data Requirement: </b><DisplayFromFHIRDataRequirement dataRequirement={dataRequirement} />
                                        </div>
                                    })}
                            </>
                    }
                </div>
            </div>
        </>}
    </div>

}

export default LibraryDisplay;